import { ConfirmationDialog, InputConfirmation } from '@dittolive/anvil'
import React, { useState } from 'react'

type Props = {
  /** Indicates whether the modal is open or closed. */
  show: boolean
  /** The app/database name for confirmation when disabling auth */
  appName: string
  /** Callback for closing the modal */
  onClose: () => void
  /** Callback for confirming the modal */
  onConfirm: () => void
  /**
   * State to which the authentication mode is being transitioned.
   * True if a request has been made to switch authentication mode on.
   */
  toAuthenticationEnabled: boolean
  /**
   * Optional terminology overrides
   */
  terminology?: {
    databaseText?: string
  }
}

/**
 * Authentication mode changing confirmation modal.
 * When enabling auth, shows a simple confirmation.
 * When disabling auth, requires typing the app name to confirm.
 */
export default function ChangeAuthenticationModeModal({
  show,
  appName,
  onClose,
  onConfirm,
  toAuthenticationEnabled,
  terminology,
}: Props) {
  const [isMatch, setIsMatch] = useState(false)

  const databaseText = terminology?.databaseText ?? 'app'
  const currentAction = toAuthenticationEnabled ? 'Enable' : 'Disable'

  const enableAuthText =
    "? You'll need to configure authentication provider webhooks to grant tokens and validate permissions."

  const renderDescription = () => {
    if (toAuthenticationEnabled) {
      return (
        <>
          Are you sure you want to enable authentication for your {databaseText}
          {enableAuthText}
        </>
      )
    } else {
      return (
        <>
          Are you sure you want to disable authentication for your{' '}
          {databaseText}? This action{' '}
          <span className="text-foreground-normal font-medium">
            is not recommended
          </span>
          , since it grants full access to your data for anyone connecting.
        </>
      )
    }
  }

  const title = `${currentAction} authentication`

  return (
    <ConfirmationDialog
      size="md"
      open={show}
      onCancel={onClose}
      onConfirm={onConfirm}
      confirmButtonText={title}
      title={title}
      confirmButtonDisabled={!toAuthenticationEnabled && !isMatch}
      variant={toAuthenticationEnabled ? 'default' : 'warning'}
      description={renderDescription()}
    >
      {!toAuthenticationEnabled && (
        <InputConfirmation value={appName} onMatch={setIsMatch} />
      )}
    </ConfirmationDialog>
  )
}
